import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { Toaster } from "@/components/ui/sonner";
import { Doodle } from "@/components/marketing/Decor";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { CitySelect } from "@/components/CitySelect";

export default function RegisterPage() {
  const { register } = useAuth();
  const [form, setForm] = useState({
    name: "", business: "", business_type: "Cafe", business_type_other: "", city: "",
    phone: "", email: "", password: "", gst_number: "",
    referral_code: "", signup_coupon_code: "", agree: false,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // ── Signup-coupon inline validation (debounced) ─────────────────
  // Hits the public /api/coupons/signup-preview endpoint so the
  // vendor sees "₹500 welcome credit" or the error inline instead
  // of only after submitting registration.
  const [couponState, setCouponState] = useState({ status: "idle", data: null, error: null });
  const couponDebounceRef = useRef(null);
  const previewCoupon = async (code) => {
    setCouponState({ status: "checking", data: null, error: null });
    try {
      const r = await api.previewSignupCoupon(code);
      setCouponState({ status: "valid", data: r.data, error: null });
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Coupon code is invalid.";
      setCouponState({ status: "invalid", data: null, error: msg });
    }
  };
  useEffect(() => {
    const code = (form.signup_coupon_code || "").trim();
    if (!code) {
      setCouponState({ status: "idle", data: null, error: null });
      return;
    }
    if (couponDebounceRef.current) clearTimeout(couponDebounceRef.current);
    couponDebounceRef.current = setTimeout(() => previewCoupon(code), 500);
    return () => couponDebounceRef.current && clearTimeout(couponDebounceRef.current);
  }, [form.signup_coupon_code]);

  const update = (k, v) =>
    setForm((s) => {
      const next = { ...s, [k]: v };
      // Whenever the vendor switches AWAY from "Other", clear the free-text
      // field so we don't send stale data. When they switch TO "Other", the
      // field is revealed below and starts empty (nothing to clear).
      if (k === "business_type" && v !== "Other") {
        next.business_type_other = "";
      }
      return next;
    });
  const phoneOk = /^\+?[0-9 ()-]{10,}$/.test(form.phone);
  const otherOk = form.business_type !== "Other" || form.business_type_other.trim().length > 0;
  const valid =
    form.name && form.business && form.business_type && otherOk && form.city &&
    phoneOk && /\S+@\S+\.\S+/.test(form.email) && form.password.length >= 8 && form.agree;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      if (form.business_type === "Other" && !form.business_type_other.trim()) {
        setErr("Please specify your business category.");
        return;
      }
      setErr("Please fill all required fields correctly (password 8+ chars, valid phone & email, T&C accepted)");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const { agree: _agree, ...rest } = form;
      // Only send business_type_other when relevant; strip empty string.
      const payload = { ...rest };
      if (payload.business_type !== "Other") {
        delete payload.business_type_other;
      } else {
        payload.business_type_other = payload.business_type_other.trim();
      }
      // Referral code is optional — strip when empty so the backend
      // sees `null` and skips redemption entirely. When present, we
      // normalize to uppercase locally as a small UX polish; the
      // backend also uppercases before lookup.
      const refRaw = (payload.referral_code || "").trim();
      if (refRaw) {
        payload.referral_code = refRaw.toUpperCase();
      } else {
        delete payload.referral_code;
      }
      // Signup billing-coupon (Phase 21) — optional. Same
      // uppercase-and-strip UX as the referral code. Unlike referral
      // codes, a bad signup coupon does NOT block registration —
      // the backend soft-fails and returns a rejected outcome that
      // we surface as a toast.
      const sigRaw = (payload.signup_coupon_code || "").trim();
      if (sigRaw) {
        payload.signup_coupon_code = sigRaw.toUpperCase();
      } else {
        delete payload.signup_coupon_code;
      }
      const res = await register(payload);
      // Toast the signup-coupon outcome so the vendor sees the
      // welcome credit that just landed (or the soft-fail reason).
      if (res?.signup_coupon_outcome) {
        const out = res.signup_coupon_outcome;
        if (out.status === "credited") {
          toast.success(
            `Welcome! ₹${Number(out.grant_rupees || 0).toFixed(0)} promo credit added to your wallet.`,
            { duration: 6000 },
          );
        } else if (out.status === "rejected") {
          toast.warning(
            out.message || "Signup coupon could not be applied — your account is still ready to use.",
            { duration: 6000 },
          );
        }
      }
      navigate("/vendor/dashboard", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Crown className="hidden md:block absolute left-8 top-6 h-12 w-16 text-[var(--graffiti-yellow-deep)] -rotate-6" />
      <Doodle.Star className="hidden md:block absolute right-12 top-12 h-10 w-10 text-black/80" />
      <Doodle.Heart className="hidden md:block absolute right-8 bottom-14 h-10 w-10 text-[var(--graffiti-yellow-deep)] rotate-12" />

      <div className="w-full max-w-xl relative">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="CityDooh home">
            <Logo size={150} light />
          </Link>
        </div>
        <div className="mkt-sticker p-7 relative">
          <span className="mkt-tape absolute -top-4 left-6">Get Started</span>
          <h1 className="font-display uppercase text-3xl mt-3 leading-none">
            Create your <span className="mkt-highlight">account</span>
          </h1>
          <p className="text-sm text-black/70 mt-2">
            We'll review and activate your account within a few hours.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full name *"><Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="reg-name" className="h-10 border-2 border-black bg-white" /></Field>
              <Field label="Business name *"><Input value={form.business} onChange={(e) => update("business", e.target.value)} data-testid="reg-business" className="h-10 border-2 border-black bg-white" /></Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Business type *">
                <Select value={form.business_type} onValueChange={(v) => update("business_type", v)}>
                  <SelectTrigger data-testid="reg-business-type" className="h-10 border-2 border-black bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="City *">
                <CitySelect
                  value={form.city}
                  onChange={(name) => update("city", name)}
                  placeholder="Select your city…"
                  triggerClassName="h-10 border-2 border-black bg-white"
                  testIdPrefix="reg-city"
                />
              </Field>
            </div>
            {form.business_type === "Other" && (
              <Field label="Please specify your business category *">
                <Input
                  value={form.business_type_other}
                  onChange={(e) => update("business_type_other", e.target.value)}
                  placeholder="e.g. Pet supplies, Bookstore, Tiffin service"
                  data-testid="reg-business-type-other"
                  className="h-10 border-2 border-black bg-white"
                  maxLength={80}
                />
              </Field>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Phone (+91) *"><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 9XXXXXXXXX" data-testid="reg-phone" className="h-10 border-2 border-black bg-white" /></Field>
              <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="reg-email" className="h-10 border-2 border-black bg-white" /></Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Password *</Label>
                <PasswordInput value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="reg-password" className="h-10 border-2 border-black bg-white" />
                <p className={`text-xs font-bold ${form.password.length >= 8 ? "text-[#176b3a]" : "text-black/55"}`}>
                  {form.password.length >= 8 ? "✓ Strong enough" : "Min 8 characters"}
                </p>
              </div>
              <Field label="GST number (optional)"><Input value={form.gst_number} onChange={(e) => update("gst_number", e.target.value)} data-testid="reg-gst" className="h-10 border-2 border-black bg-white" /></Field>
            </div>
            <Field label="Referral code (optional)">
              <Input
                value={form.referral_code}
                onChange={(e) => update("referral_code", e.target.value.toUpperCase())}
                placeholder="E.g. SUMMER30"
                data-testid="reg-referral-code"
                className="h-10 border-2 border-black bg-white uppercase tracking-wider"
                maxLength={32}
              />
              <p className="text-[11px] text-black/55 mt-1">
                Have a code from CityDooh? Enter it to unlock a free access window.
                Invalid codes will block sign-up so you don't accidentally create
                an account expecting a deal that isn't there.
              </p>
            </Field>
            <Field label="Welcome / signup coupon (optional)">
              <Input
                value={form.signup_coupon_code}
                onChange={(e) => update("signup_coupon_code", e.target.value.toUpperCase())}
                placeholder="E.g. WELCOME500"
                data-testid="reg-signup-coupon"
                className={`h-10 border-2 bg-white uppercase tracking-wider ${
                  couponState.status === "valid" ? "border-[#176b3a]" :
                  couponState.status === "invalid" ? "border-[#8b1212]" : "border-black"
                }`}
                maxLength={32}
              />
              {couponState.status === "checking" && (
                <p className="text-[11px] text-black/55 mt-1 flex items-center gap-1" data-testid="signup-coupon-checking">
                  <Loader2 className="h-3 w-3 animate-spin" /> Checking code…
                </p>
              )}
              {couponState.status === "valid" && couponState.data && (
                <p className="text-[11px] font-semibold text-[#176b3a] mt-1 flex items-center gap-1" data-testid="signup-coupon-valid">
                  <CheckCircle2 className="h-3 w-3" />
                  {couponState.data.message}
                </p>
              )}
              {couponState.status === "invalid" && (
                <p className="text-[11px] font-semibold text-[#8b1212] mt-1 flex items-center gap-1" data-testid="signup-coupon-invalid">
                  <AlertCircle className="h-3 w-3" />
                  {couponState.error}
                </p>
              )}
              {couponState.status === "idle" && (
                <p className="text-[11px] text-black/55 mt-1">
                  Got a welcome code? Enter it to instantly credit your promo wallet.
                  A bad code won't block sign-up — your account will still be created.
                </p>
              )}
            </Field>
            <label className="flex items-start gap-2 text-sm text-black/80 pt-1">
              <Checkbox checked={form.agree} onCheckedChange={(v) => update("agree", !!v)} data-testid="reg-agree" className="border-2 border-black" />
              <span>I agree to CityDooh's Terms &amp; Conditions and Privacy Policy.</span>
            </label>
            {err && (
              <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="reg-error">{err}</p>
            )}
            <button type="submit" className="mkt-btn w-full justify-center" disabled={busy} data-testid="reg-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Create account <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>
          <p className="text-sm text-black/70 mt-5 text-center">
            Already have an account?{" "}
            <Link to="/vendor/login" className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="uppercase text-[11px] font-bold tracking-wider text-black">{label}</Label>
    {children}
  </div>
);
